<template>
    <div>
        <rkm-alert v-if="showIndicadores">
            <ul class="m-0">
                <li v-if="isRecemNascidoCrianca">
                    C2: Este cidadão está no público de desenvolvimento infantil. Para visitas deste acompanhamento,
                    utilize <strong>Recém-nascido</strong> ou <strong>Criança</strong>, conforme o contexto da visita.
                </li>

                <li v-if="isGestantePuerpera">
                    C3: Este cidadão possui contexto gestacional/puerperal ativo. Para visitas deste acompanhamento,
                    utilize <strong>Gestante</strong> ou <strong>Puérpera</strong>, conforme a finalidade da visita.
                </li>

                <li v-if="isDiabetico">
                    C4: Este cidadão possui condição ativa compatível com diabetes. Para visitas deste acompanhamento,
                    utilize <strong>Pessoa com diabetes</strong>.
                </li>

                <li v-if="isHipertenso">
                    C5: Este cidadão possui condição ativa compatível com hipertensão. Para visitas deste acompanhamento,
                    utilize <strong>Pessoa com hipertensão</strong>.
                </li>

                <li v-if="isIdoso">
                    C6: Este cidadão está no público da pessoa idosa. Para visitas deste acompanhamento,
                    utilize <strong>Pessoa idosa</strong> quando a finalidade da visita corresponder a esse cuidado.
                </li>
            </ul>
        </rkm-alert>

        <rkm-form
            ref="Form"
            show-legend
            v-if="loaded"
        >
            <rkm-area
                type="fieldset"
                title="Identificação do paciente"
            >
                <FormCidadao
                    :data="{ form, model }"
                />
            </rkm-area>

            <rkm-area
                type="fieldset"
                title="Profissional responsável"
                show-legend
            >
                <FormProfissional
                    :data="{ form, model }"
                />
            </rkm-area>

            <rkm-area
                type="fieldset"
                title="Atendimento"
            >
                <FormAtendimento
                    :data="{ form, model }"
                />
            </rkm-area>

            <rkm-area
                v-if="model.cidadao && model.cidadao.idadeAnos >= 60"
                title="IVCF - Índice de vulnerabilidade clínico-funcional"
                type="fieldset"
            >
                <rkm-row>
                    <rkm-col>
                        <p>
                            As perguntas são direcionadas à pessoa idosa e, quando possível, devem ser confirmadas pelo familiar ou acompanhante, desde
                            que convivam com a pessoa idosa e estejam em condições de responder aos questionamentos. Nas pessoas idosas incapazes de
                            responder, utilizar as respostas do cuidador.
                        </p>
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col>
                        <rkm-alert>Em geral o tempo de aplicação deste instrumento varia de 5 a 10 minutos.</rkm-alert>
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col>
                        <rkm-form-radio-group
                            @change="handleDesejaPreencherChange"
                            label="Deseja preencher o IVCF 20 para esta pessoa?"
                            v-model="model.atendimento.ivcf.desejaPreencher"
                        >
                            <rkm-form-radio :value="true">Sim</rkm-form-radio>
                            <rkm-form-radio :value="false">Nao</rkm-form-radio>
                        </rkm-form-radio-group>
                    </rkm-col>
                </rkm-row>

                <template v-if="model.atendimento.ivcf.desejaPreencher">
                    <rkm-row>
                        <rkm-col>
                            <h5>Perguntas do instrumento</h5>
                        </rkm-col>
                    </rkm-row>

                    <rkm-area title="Idade do cidadão | Grupo 1 de 8">
                        <rkm-form-input
                            :value="`${model.cidadao.idadeAnos} anos e ${model.cidadao.idadeMeses} meses.`"
                            name="pacienteIdade"
                            readonly
                        />
                    </rkm-area>

                    <rkm-area no-margin-top title="Percepção da saúde | Grupo 2 de 8">
                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.estadoSaudeGeral"
                                    label="Em geral, comparando com outras pessoas de sua idade, você diria que sua saúde é"
                                    v-model="model.atendimento.ivcf.estadoSaudeGeral"
                                >
                                    <rkm-form-radio value="excelente_muito_boa_ou_boa">Excelente, muito boa ou boa</rkm-form-radio>
                                    <rkm-form-radio value="regular_ou_ruim">Regular ou ruim</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>
                    </rkm-area>

                    <rkm-area no-margin-top title="Atividades de vida diária | Grupo 3 de 8">
                        <rkm-row>
                            <rkm-col>
                                <strong class="text-info">Atividades de vida diária instrumental</strong>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <p>Por causa de sua saúde ou condição física,</p>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.limitacaoCompras"
                                    label="Você deixou de fazer compras?"
                                    v-model="model.atendimento.ivcf.limitacaoCompras"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não ou não faz compras por outros motivos que não a saúde</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.limitacaoGestaoFinanceira"
                                    label="Você deixou de controlar seu dinheiro, gastos ou pagar as contas de sua casa?"
                                    v-model="model.atendimento.ivcf.limitacaoGestaoFinanceira"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não ou não controla o dinheiro por outros motivos que não a saúde</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.limitacaoTarefasDomesticas"
                                    label="Você deixou de realizar pequenos trabalhos domésticos, como lavar a louça, arrumar a casa ou fazer limpeza leve?"
                                    v-model="model.atendimento.ivcf.limitacaoTarefasDomesticas"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não ou não faz mais pequenos trabalhos domésticos por outros motivos que não a saúde</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <strong class="text-info">Atividades de vida diária básica</strong>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.banhoIndependencia"
                                    label="Por causa de sua saúde ou condição física, você deixou de tomar banho sozinho?"
                                    v-model="model.atendimento.ivcf.banhoIndependencia"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>
                    </rkm-area>

                    <rkm-area no-margin-top title="Cognição | Grupo 4 de 8">
                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.relatoEsquecimentoPorTerceiros"
                                    label="Algum familiar ou amigo falou que você está ficando esquecido?"
                                    v-model="model.atendimento.ivcf.relatoEsquecimentoPorTerceiros"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.esquecimentoPiorando"
                                    label="Você tem um esquecimento que está piorando nos últimos meses?"
                                    v-model="model.atendimento.ivcf.esquecimentoPiorando"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.esquecimentoImpactaAtividades"
                                    label="Você tem um esquecimento que está impedindo a realização de alguma atividade do cotidiano?"
                                    v-model="model.atendimento.ivcf.esquecimentoImpactaAtividades"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>
                    </rkm-area>

                    <rkm-area no-margin-top title="Humor | Grupo 5 de 8">
                        <rkm-row>
                            <rkm-col>
                                <p>No último mês,</p>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.humorTristezaDesanimo"
                                    label="Você ficou com desânimo, tristeza ou desesperança?"
                                    v-model="model.atendimento.ivcf.humorTristezaDesanimo"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.perdaInteresseAtividades"
                                    label="Você perdeu o interesse ou prazer em atividades anteriormente prazerosas?"
                                    v-model="model.atendimento.ivcf.perdaInteresseAtividades"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>
                    </rkm-area>

                    <rkm-area no-margin-top title="Mobilidade | Grupo 6 de 8">
                        <rkm-row>
                            <rkm-col>
                                <strong class="text-info">Alcance, preensão e pinça</strong>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.limitacaoElevarBracos"
                                    label="Você é incapaz de elevar os braços acima do nível do ombro?"
                                    v-model="model.atendimento.ivcf.limitacaoElevarBracos"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.limitacaoMotricidadeFina"
                                    label="Você é incapaz de manusear ou segurar pequenos objetos?"
                                    v-model="model.atendimento.ivcf.limitacaoMotricidadeFina"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <strong class="text-info">Capacidade aeróbica e/ou muscular</strong>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.perdaPesoNaoIntencional"
                                    label="Você teve perda de peso não intencional de 4,5 kg ou 5% do peso corporal no último ano, 6 kg nos últimos 6 meses ou 3 kg no último mês?"
                                    v-model="model.atendimento.ivcf.perdaPesoNaoIntencional"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.imcAbaixo22"
                                    label="Você tem Índice de Massa Corporal (IMC) menor que 22 kg/m²?"
                                    v-model="model.atendimento.ivcf.imcAbaixo22"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.panturrilhaAbaixo31"
                                    label="Você tem a circunferência (perímetro) da panturrilha menor que 31 centímetros?"
                                    v-model="model.atendimento.ivcf.panturrilhaAbaixo31"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.marchaLenta4mMaior5s"
                                    label="Em um teste de velocidade da marcha, o tempo gasto pela pessoa idosa para percorrer 4 metros é maior do que 5 segundos?"
                                    v-model="model.atendimento.ivcf.marchaLenta4mMaior5s"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <strong class="text-info">Marcha</strong>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.dificuldadeCaminharImpactoAtividades"
                                    label="Você tem dificuldade para caminhar que possa impedir a realização de alguma atividade do cotidiano?"
                                    v-model="model.atendimento.ivcf.dificuldadeCaminharImpactoAtividades"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.quedasRecorrentes"
                                    label="Você teve duas ou mais quedas no último ano?"
                                    v-model="model.atendimento.ivcf.quedasRecorrentes"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <strong class="text-info">Continência esfincteriana</strong>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.incontinencia"
                                    label="Você perde urina ou fezes, sem querer, em algum momento?"
                                    v-model="model.atendimento.ivcf.incontinencia"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>
                    </rkm-area>

                    <rkm-area no-margin-top title="Comunicação | Grupo 7 de 8">
                        <rkm-row>
                            <rkm-col>
                                <strong class="text-info">Visão</strong>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.problemaVisaoImpactoAtividades"
                                    label="Você tem problemas de visão capazes de impedir a realização de alguma atividade do cotidiano? (É permitido o uso de óculos ou lentes de contato)"
                                    v-model="model.atendimento.ivcf.problemaVisaoImpactoAtividades"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <strong class="text-info">Audição</strong>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.problemaAudicaoImpactoAtividades"
                                    label="Você tem problemas de audição capazes de impedir a realização de alguma atividade do cotidiano? (É permitido o uso de aparelhos de audição)"
                                    v-model="model.atendimento.ivcf.problemaAudicaoImpactoAtividades"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>
                    </rkm-area>

                    <rkm-area no-margin-top title="Comorbidade múltipla | Grupo 8 de 8">
                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.doencasCronicasMultiplas"
                                    label="Você tem cinco ou mais doenças crônicas?"
                                    v-model="model.atendimento.ivcf.doencasCronicasMultiplas"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.polifarmacia5OuMaisMedicamentos"
                                    label="Você faz uso regular de cinco ou mais medicamentos diferentes todos os dias?"
                                    v-model="model.atendimento.ivcf.polifarmacia5OuMaisMedicamentos"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-radio-group
                                    :validations="validations.ivcf.internacaoUltimos6Meses"
                                    label="Você teve alguma internação nos últimos 6 meses?"
                                    v-model="model.atendimento.ivcf.internacaoUltimos6Meses"
                                >
                                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-col>
                        </rkm-row>
                    </rkm-area>

                    <template v-if="pontuacaoIvcf > 0">
                        <rkm-area no-margin-top title="Pontuação final do IVCF 20">
                            <rkm-alert
                                v-if="pontuacaoIvcf <= 6"
                                color="success"
                            >
                                Idoso com BAIXO risco de vulnerabilidade Clínico-funcional – IDOSO ROBUSTO
                            </rkm-alert>

                            <rkm-alert
                                v-if="pontuacaoIvcf >= 7 && pontuacaoIvcf <= 14"
                                color="warning"
                            >
                                Idoso com MODERADO risco de vulnerabilidade Clínico-funcional – IDOSO EM RISCO DE FRAGILIZAÇÃO
                            </rkm-alert>

                            <rkm-alert
                                v-if="pontuacaoIvcf >= 15"
                                color="danger"
                            >
                                Idoso com ALTO risco de vulnerabilidade Clínico-funcional – IDOSO FRÁGIL
                            </rkm-alert>
                        </rkm-area>
                    </template>
                </template>
            </rkm-area>

            <rkm-area
                type="fieldset"
                title="Motivo da visita"
            >
                <FormMotivo
                    :data="{ form, model }"
                />
            </rkm-area>

            <FormComplemento
                :data="{ form, model }"
            />
        </rkm-form>

        <Modal
            ref="Modal"
            origem="atendimento"
            :residentes="residentes"
            @closeModal="closeModal"
            :data="{ form, model }"
        />
    </div>
</template>

<script>

// Mixins
import { repository, stringCase, data, object, formats, error } from "@/mixins";

// Vuex.
import { mapActions } from "vuex";

// Components
import FormCidadao from './components/cidadao/Form.vue'
import FormProfissional from './components/profissional/Form.vue'
import FormAtendimento from './components/atendimento/Form.vue'
import FormMotivo from './components/motivo/Form.vue'
import FormComplemento from './components/complemento/Form.vue'
import Modal from './Modal.vue'

export default {
    components: {
        FormCidadao,
        FormProfissional,
        FormAtendimento,
        FormMotivo,
        FormComplemento,
        Modal
    },
    computed: {
        validations() {

            return {
                complemento: { max: 1000 },
                peso: { min_value: 0.5, max_value: 500 },
                altura: { min_value: 20, max_value: 250 },
                ivcf: {
                    estadoSaudeGeral: { required: true },
                    limitacaoCompras: { required: true },
                    limitacaoGestaoFinanceira: { required: true },
                    limitacaoTarefasDomesticas: { required: true },
                    banhoIndependencia: { required: true },
                    relatoEsquecimentoPorTerceiros: { required: true },
                    esquecimentoPiorando: { required: true },
                    esquecimentoImpactaAtividades: { required: true },
                    humorTristezaDesanimo: { required: true },
                    perdaInteresseAtividades: { required: true },
                    limitacaoElevarBracos: { required: true },
                    limitacaoMotricidadeFina: { required: true },
                    perdaPesoNaoIntencional: { required: true },
                    imcAbaixo22: { required: true },
                    panturrilhaAbaixo31: { required: true },
                    marchaLenta4mMaior5s: { required: true },
                    dificuldadeCaminharImpactoAtividades: { required: true },
                    quedasRecorrentes: { required: true },
                    incontinencia: { required: true },
                    problemaVisaoImpactoAtividades: { required: true },
                    problemaAudicaoImpactoAtividades: { required: true },
                    doencasCronicasMultiplas: { required: true },
                    polifarmacia5OuMaisMedicamentos: { required: true },
                    internacaoUltimos6Meses: { required: true },
                },
            }
        },
        showIndicadores() {
            return this.isRecemNascidoCrianca || this.isGestantePuerpera || this.isDiabetico || this.isHipertenso || this.isIdoso
        },
        isRecemNascidoCrianca() {
            return this.additionals && this.additionals.isRecemNascidoCrianca
        },
        isGestantePuerpera() {
            return this.additionals && this.additionals.isGestantePuerpera
        },
        isDiabetico() {
            return this.additionals && this.additionals.isDiabetico
        },
        isHipertenso() {
            return this.additionals && this.additionals.isHipertenso
        },
        isIdoso() {
            return this.additionals && this.additionals.isIdoso
        },
        pontuacaoIvcf() {
            let pontuacao = 0

            if (this.model.cidadao.idadeAnos >= 75 && this.model.cidadao.idadeAnos <= 84) {
                pontuacao += 1
            }
            else if (this.model.cidadao.idadeAnos >= 85) {
                pontuacao += 3
            }

            if (this.model.atendimento.ivcf.estadoSaudeGeral === 'regular_ou_ruim') {
                pontuacao += 1
            }

            if (this.model.atendimento.ivcf.limitacaoCompras || this.model.atendimento.ivcf.limitacaoGestaoFinanceira || this.model.atendimento.ivcf.limitacaoTarefasDomesticas) {
                pontuacao += 4
            }

            if (this.model.atendimento.ivcf.banhoIndependencia) {
                pontuacao += 6
            }

            if (this.model.atendimento.ivcf.relatoEsquecimentoPorTerceiros) {
                pontuacao += 1
            }

            if (this.model.atendimento.ivcf.esquecimentoPiorando) {
                pontuacao += 1
            }

            if (this.model.atendimento.ivcf.esquecimentoImpactaAtividades) {
                pontuacao += 2
            }

            if (this.model.atendimento.ivcf.humorTristezaDesanimo) {
                pontuacao += 2
            }

            if (this.model.atendimento.ivcf.perdaInteresseAtividades) {
                pontuacao += 2
            }

            if (this.model.atendimento.ivcf.limitacaoElevarBracos) {
                pontuacao += 1
            }

            if (this.model.atendimento.ivcf.limitacaoMotricidadeFina) {
                pontuacao += 1
            }

            if (this.model.atendimento.ivcf.perdaPesoNaoIntencional || this.model.atendimento.ivcf.imcAbaixo22 || this.model.atendimento.ivcf.panturrilhaAbaixo31 || this.model.atendimento.ivcf.marchaLenta4mMaior5s) {
                pontuacao += 2
            }

            if (this.model.atendimento.ivcf.dificuldadeCaminharImpactoAtividades) {
                pontuacao += 2
            }

            if (this.model.atendimento.ivcf.quedasRecorrentes) {
                pontuacao += 2
            }

            if (this.model.atendimento.ivcf.incontinencia) {
                pontuacao += 2
            }

            if (this.model.atendimento.ivcf.problemaVisaoImpactoAtividades) {
                pontuacao += 2
            }

            if (this.model.atendimento.ivcf.problemaAudicaoImpactoAtividades) {
                pontuacao += 2
            }

            if (this.model.atendimento.ivcf.doencasCronicasMultiplas || this.model.atendimento.ivcf.polifarmacia5OuMaisMedicamentos || this.model.atendimento.ivcf.internacaoUltimos6Meses) {
                pontuacao += 4
            }

            return pontuacao
        },
    },
    created() {

        this.repositories = {
            visitaDomiciliar: this.getConectaSusRepository('atencaoBasica.visitaDomiciliar')
        }

        this.load()
    },
    data: () => ({
        additionals: null,
        form: {},
        model: {},
        loaded: false,
        residentes: []
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {

            this.setLoading()

            if (this.id) {

                this.repositories.visitaDomiciliar.edit(this.id)
                    .then(response => {
                        this.additionals = Object.assign(response.data.additionals)
                        this.model = Object.assign(response.data.model)
                        this.form = Object.assign(response.data.form)

                        this.loaded = true

                        this.$emit('loaded', {additionals: this.additionals, model: this.model})
                    })
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            } else {

                this.repositories.visitaDomiciliar.show(this.pacienteId)
                    .then(response => {
                        this.additionals = Object.assign(response.data.additionals)
                        this.model = Object.assign(response.data.model)
                        this.form = Object.assign(response.data.form)

                        this.loaded = true

                        this.$emit('loaded', {additionals: this.additionals, model: this.model})
                    })
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            }
        },
        async save() {

            if(this.hasVisitasDuplicadas()){
                this.showWarningNotification("Já existe uma visita domiciliar cadastrada para este profissional, turno e data.")
                return
            }

            const valid = await this.$refs.Form.validate()
            if (!valid) return
            this.setLoading()

            try {
                const { data } = await this.repositories.visitaDomiciliar.store(this.prepareData())
                await this.getResidentes()

                if (this.residentes.length > 0) {
                    this.showConfirmation(
                        'Registro salvo. Deseja replicar as informações para outros residentes?',
                        async () => {
                            this.$refs.Modal.open({ id: this.model.cidadao.pacienteId })
                        },
                        async () => {
                            this.showSaveNotification()
                            this.$emit('save', data)
                        }
                    )
                } else {
                    this.showSaveNotification()
                    this.$emit('save', data)
                }

            } catch (error) {
                this.handleError(error)
            } finally {
                this.unsetLoading()
            }
        },
        async getResidentes() {
            this.setLoading()

            const request = {
                pacienteId: this.model.cidadao.pacienteId,
            }

            try {
                const response = await this.repositories.visitaDomiciliar.getResidentes(this.camelObjectKeysToSnakeRecursive(request))
                this.residentes = Object.assign(response.data)
            } catch (error) {
                this.handleError(error)
            } finally {
                this.unsetLoading()
            }
        },
        hasVisitasDuplicadas(){
            const profissionalId = Number(this.model.cadastro.usuario.id)
            const turnoId = Number(this.model.atendimento.turnoId)
            const atendimentoId = Number(this.model.atendimento.id)
            const data =  this.getFormatedDate(this.model.cadastro.dataUsuarioResponsavel, 'YYYY-MM-DD')

            const duplicadas = this.visitasRealizadas.filter(visita => {
                return Number(visita.profissionalId) === profissionalId  &&
                    Number(visita.turnoId) === turnoId &&
                    visita.data === data &&
                    Number(visita.id) != atendimentoId
            })

            return duplicadas.length > 0
        },
        prepareData() {

            const pacienteId = this.model.cidadao.pacienteId
            const cadastro = this.clone(this.model.cadastro)

            cadastro.usuarioId = cadastro.usuario.id
            cadastro.dataUsuarioResponsavel = this.getFormatedDate(cadastro.dataUsuarioResponsavel, 'YYYY-MM-DD')

            const atendimento = this.clone(this.model.atendimento)
            const complemento = this.clone(this.model.complemento)
            const motivo = this.clone(this.model.motivo)

            return this.camelObjectKeysToSnakeRecursive({
                pacienteId, cadastro, atendimento, motivo, complemento
            })
        },
        closeModal() {
            this.$emit('save')
        },
        handleDesejaPreencherChange(value) {
            if (value === false) {
                this.showConfirmation(
                    'Deseja realmente descartar os dados já preenchidos do IVFC 20?',
                    async () => {
                        this.model.atendimento.ivcf.estadoSaudeGeral = false
                        this.model.atendimento.ivcf.limitacaoCompras = false
                        this.model.atendimento.ivcf.limitacaoGestaoFinanceira = false
                        this.model.atendimento.ivcf.limitacaoTarefasDomesticas = false
                        this.model.atendimento.ivcf.banhoIndependencia = false
                        this.model.atendimento.ivcf.relatoEsquecimentoPorTerceiros = false
                        this.model.atendimento.ivcf.esquecimentoPiorando = false
                        this.model.atendimento.ivcf.esquecimentoImpactaAtividades = false
                        this.model.atendimento.ivcf.humorTristezaDesanimo = false
                        this.model.atendimento.ivcf.perdaInteresseAtividades = false
                        this.model.atendimento.ivcf.limitacaoElevarBracos = false
                        this.model.atendimento.ivcf.limitacaoMotricidadeFina = false
                        this.model.atendimento.ivcf.perdaPesoNaoIntencional = false
                        this.model.atendimento.ivcf.imcAbaixo22 = false
                        this.model.atendimento.ivcf.panturrilhaAbaixo31 = false
                        this.model.atendimento.ivcf.marchaLenta4mMaior5s = false
                        this.model.atendimento.ivcf.dificuldadeCaminharImpactoAtividades = false
                        this.model.atendimento.ivcf.quedasRecorrentes = false
                        this.model.atendimento.ivcf.incontinencia = false
                        this.model.atendimento.ivcf.problemaVisaoImpactoAtividades = false
                        this.model.atendimento.ivcf.problemaAudicaoImpactoAtividades = false
                        this.model.atendimento.ivcf.doencasCronicasMultiplas = false
                        this.model.atendimento.ivcf.polifarmacia5OuMaisMedicamentos = false
                        this.model.atendimento.ivcf.internacaoUltimos6Meses = false
                    },
                    async () => {
                        this.model.atendimento.ivcf.desejaPreencher = true
                    }
                )
            }
        },
    },
    mixins: [repository, stringCase, data, object, formats, error],
    props: {
        id: { type: Number, default: null },
        pacienteId: { type: Number, default: null },
        visitasRealizadas: { type: Array, default: null}
    },
}
</script>